import type { PlanInfo } from "schemas/billing";
import type { BillingInterval } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";
import type { BillingModalProps } from "../AppModalTypes";

import * as React from "react";
import { useState } from "react";

import { ConnectShopifyCallout } from "@editor/components/editor/page/ConnectShopifyCallout";
import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import {
  useSubscriptionDetails,
  useSubscriptionInfo,
} from "@editor/hooks/subscription";
import { useIsWorkspaceOwner } from "@editor/hooks/useIsWorkspaceOwner";
import { useModal } from "@editor/hooks/useModal";
import { selectDraftElementIsPublished } from "@editor/reducers/core-reducer";
import { useGetBillingInformationQuery } from "@editor/reducers/sanity-reducer";
import { useEditorSelector } from "@editor/store";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import Switch from "@replo/design-system/components/switch/Switch";
import twMerge from "@replo/design-system/utils/twMerge";
import { Check } from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { pluralize } from "replo-utils/lib/pluralize";
import {
  BILLING_PLAN_CONFIRMATION_PARAM,
  BILLING_PLAN_CONFIRMATION_TYPE_PARAM,
  BillingTiers,
  getBillingPlanInfoByTier,
  getNonCustomBillingPlans,
} from "schemas/billing";

import PlanCard from "../../../features/workspace/components/PlanCard";
import { getPlanColorTheme } from "../../../features/workspace/components/planUtils";
import { BillingConfirmation } from "./shared/BillingConfirmation";

const getMessage = ({
  key,
  isElementPublished,
  usage,
}: {
  key?: string;
  usage?: { current: number; maximum: number };
  isElementPublished?: boolean;
}): { primary: string; secondary?: string } => {
  switch (key) {
    case "billingPlan.experimentActivationAttempt":
      return {
        primary: "Upgrade your workspace to launch Experiments",
      };
    case "billingPlan.analytics":
      return {
        primary: "Upgrade your workspace to access Analytics",
        secondary:
          "Access key ecommerce metrics and data insights to understand your store and your users.",
      };
    case "billingPlan.projectMembershipLimitExceeded":
      return {
        primary:
          "You've reached a limit on the collaborators allowed for this plan.",
        secondary: "Please upgrade from your current plan.",
      };
    case "billingPlan.entitlements.insufficient.ai.credits":
      return {
        primary: "You've reached a limit on the Replo AI usage for this plan.",
        secondary:
          "Please upgrade from your current plan or wait until the next billing cycle to continue.",
      };
    case "billingPlan.entitlements.insufficient.elements.publishedElements":
    case "billingPlan.entitlements.insufficient.elements.publishedPages":
    case "billingPlan.entitlements.insufficient.elements.publishedSections":
    case "billingPlan.entitlements.insufficient.elements.publishedProductTemplates": {
      const elementNameSingular = exhaustiveSwitch({ type: key })({
        "billingPlan.entitlements.insufficient.elements.publishedElements":
          "item",
        "billingPlan.entitlements.insufficient.elements.publishedPages": "page",
        "billingPlan.entitlements.insufficient.elements.publishedSections":
          "section",
        "billingPlan.entitlements.insufficient.elements.publishedProductTemplates":
          "product template",
      });
      const elementNamePlural = exhaustiveSwitch({ type: key })({
        "billingPlan.entitlements.insufficient.elements.publishedElements":
          "items",
        "billingPlan.entitlements.insufficient.elements.publishedPages":
          "pages",
        "billingPlan.entitlements.insufficient.elements.publishedSections":
          "sections",
        "billingPlan.entitlements.insufficient.elements.publishedProductTemplates":
          "product templates",
      });
      if (usage && usage.maximum !== 0) {
        const elementsToUnpublish =
          usage.current - usage.maximum + (isElementPublished ? 0 : 1);
        return {
          primary: `You've reached the limit on published ${elementNameSingular}s allowed for this plan.`,
          secondary: `Please upgrade from your current plan or unpublish ${elementsToUnpublish} ${pluralize({ singular: elementNameSingular, plural: elementNamePlural, count: elementsToUnpublish })} to continue.`,
        };
      }
      return {
        primary: `Upgrade to publish this ${elementNameSingular}`,
      };
    }
    case "billingPlan.entitlements.insufficient.integrations.shopify": {
      const isAtOrOverLimit = usage && usage.current >= usage.maximum;
      if (isAtOrOverLimit) {
        const storesToDisconnect = usage.current - usage.maximum + 1;
        return {
          primary:
            "You've reached the limit of the number of Shopify stores you can connect.",
          secondary: `Please upgrade from your current plan or disconnect ${pluralize({ singular: "store", plural: "stores", count: storesToDisconnect })} to continue.`,
        };
      }
      return {
        primary:
          "You've hit the integration limit with your current billing method",
        secondary:
          "To have more integrations available on your plan, please contact support via chat or support@replo.app to enable these integrations.",
      };
    }
    default:
  }
  return {
    primary: "Upgrade for More Features",
    secondary: "Compare our plans.",
  };
};

export const BillingModal = (props: BillingModalProps) => {
  // NOTE (Gabe 2024-03-25): This Modal can be used either from within the
  // editor, where a project is selectable, or from the workspace route where a
  // workspaceId is passed in the URL. We need to handle both cases.

  // Note (Evan, 2024-03-28): If the billing plan confirmation param is set, just
  // render the confirmation component
  const [searchParams] = useSearchParams();
  const isBillingPlanConfirmed =
    searchParams.get(BILLING_PLAN_CONFIRMATION_PARAM) === "true";

  const workspace = useCurrentWorkspace();

  if (isBillingPlanConfirmed) {
    return <BillingConfirmationModal />;
  }

  return <InnerBillingModal workspace={workspace ?? undefined} {...props} />;
};

const BillingConfirmationModal = () => {
  const modal = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirmationType = searchParams.get(
    BILLING_PLAN_CONFIRMATION_TYPE_PARAM,
  );

  const { title, body } = (() => {
    if (confirmationType === "cancel") {
      return {
        title: "You successfully canceled your plan.",
        body: "We'll miss you! Don't forget, our free tier is always here for you. 👋🏻",
      };
    }
    if (confirmationType === "upgrade") {
      return {
        title: "Congratulations!",
        body: "You just upgraded your plan. 🥳",
      };
    }
    if (confirmationType === "downgrade") {
      return {
        title: "",
        body: "",
      };
    }
    return {
      title: "Congratulations!",
      body: "You've updated your plan. 🥳",
    };
  })();

  const onClose = () => {
    setSearchParams(
      (params) => {
        params.delete(BILLING_PLAN_CONFIRMATION_PARAM);
        if (params.has(BILLING_PLAN_CONFIRMATION_TYPE_PARAM)) {
          params.delete(BILLING_PLAN_CONFIRMATION_TYPE_PARAM);
        }
        return new URLSearchParams(params);
      },
      { replace: true },
    );
    modal.closeModal({ type: "billingModal" });
  };

  return (
    <Modal
      isOpen
      size="lg"
      title=""
      data-testid="billing-plan-confirmation-modal"
      onOpenChange={onClose}
    >
      <BillingConfirmation title={title} body={body} />
    </Modal>
  );
};

const InnerBillingModal = ({
  billingPlanMessageKey,
  source,
  usage,
  workspace,
}: BillingModalProps & {
  workspace?: Workspace;
}) => {
  const modal = useModal();
  const workspaceId = workspace?.id;
  const params = useParams();

  const { data: subscriptionDetails } = useSubscriptionDetails(workspaceId);
  const { subscriptionInfo: currentPlan, isLoading } =
    useSubscriptionInfo(workspaceId);

  const isWorkspaceOwner = useIsWorkspaceOwner(workspaceId);

  const [isYearly, setIsYearly] = useState(true);

  const nonCustomBillingPlans = getNonCustomBillingPlans();

  // NOTE (Ben, 2023-12-29): We basically want to show only the growth plan when the user
  // isn't on the growth plan and opens this modal through the experiment activation attempt,
  // because only the growth and custom plans that get a/b testing,
  // and we don't show custom inside the modal in a line up, ever.
  const plansToExclude = (() => {
    if (billingPlanMessageKey === "billingPlan.experimentActivationAttempt") {
      return [BillingTiers.FREE];
    }
    // NOTE (Gabe 2024-03-25): We only exclude plans if this is the result of a
    // billingPlanException. This is because we only want to show upgrades when
    // a user hits a billing limit, but if they are just managing their plan the
    // should see all of them.
    if (billingPlanMessageKey) {
      if (currentPlan?.tier === BillingTiers.BASIC) {
        return [BillingTiers.BASIC];
      }
      if (currentPlan?.tier === BillingTiers.STANDARD) {
        return [BillingTiers.BASIC, BillingTiers.STANDARD];
      }
      if (currentPlan?.tier === BillingTiers.GROWTH) {
        return [BillingTiers.BASIC, BillingTiers.STANDARD, BillingTiers.GROWTH];
      }
    }
    return [];
  })();

  const filteredPlans = nonCustomBillingPlans.filter(
    (plan) => !plansToExclude.includes(plan.tier),
  );

  const isElementPublished = useEditorSelector(selectDraftElementIsPublished);

  if (isLoading) {
    return null;
  }

  let { primary: billingHeader, secondary: billingSubheader } = getMessage({
    key: billingPlanMessageKey,
    usage,
    isElementPublished,
  });

  if (!isWorkspaceOwner) {
    billingHeader =
      "You don't have permission to update the billing plan, contact your workspace owner to upgrade.";
  }

  const shouldUserConnectShopify =
    workspace?.preferredPaymentProcessor === "shopify" &&
    !workspace?.hasShopifyIntegration &&
    isWorkspaceOwner;

  if (shouldUserConnectShopify) {
    return (
      <Modal
        isOpen
        onOpenChange={(open) => {
          if (!open) {
            modal.closeModal({ type: "billingModal" });
          }
        }}
        size="sm"
        data-testid="billing-modal"
        title="Connect Shopify"
        description="To start a billing plan with Replo, first connect your Shopify store."
      >
        <ConnectShopifyCallout
          cookieValue={{
            type: "billingModal",
            workspaceId: workspace.id,
            projectId: params.projectId,
          }}
        />
      </Modal>
    );
  }

  const handleBillingIntervalChange = () => {
    setIsYearly((prev) => !prev);
  };

  const isUpgrade = (plan: PlanInfo) => {
    const planRanks = {
      free: 0,
      basic: 1,
      standard: 2,
      growth: 3,
      custom: 4,
    };

    const currentRank =
      planRanks[currentPlan.tier as keyof typeof planRanks] || 0;
    const planRank = planRanks[plan.tier as keyof typeof planRanks] || 0;

    return planRank > currentRank;
  };

  // Note (Berkley, 2025-05-07): Calculate visible plans to determine modal width
  // +1 for the custom plan (we always want to show the custom plan)
  const visiblePlans =
    filteredPlans.filter(
      (plan) =>
        plan.tier !== BillingTiers.FREE &&
        (isUpgrade(plan) || plan.tier === currentPlan.tier),
    ).length + 1;

  const gridColumnsClass = `grid-cols-${visiblePlans}`;

  // Note (Berkley, 2025-05-07): Determine modal width based on number of visible plans
  // second member of the expression scales the width based on the viewport width
  const getModalWidthClass = () => {
    switch (gridColumnsClass) {
      case "grid-cols-4":
        return "max-w-[1200px] w-[90vw]";
      case "grid-cols-3":
        return "max-w-[1000px] w-[85vw]";
      case "grid-cols-2":
        return "max-w-[800px] w-[80vw]";
      case "grid-cols-1":
      default:
        return "max-w-[600px] w-[70vw]";
    }
  };
  const isOnYearlyPlan = subscriptionDetails?.billingInterval === "yearly";

  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) {
          modal.closeModal({ type: "billingModal" });
        }
      }}
      size="custom"
      UNSAFE_className={getModalWidthClass()}
      data-testid="billing-modal"
      title={billingHeader}
      description={billingSubheader}
    >
      <div className="flex w-full flex-col">
        {!isOnYearlyPlan && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3 ml-auto">
              <span className="text-sm text-muted">Pay Monthly</span>
              <div
                onClick={handleBillingIntervalChange}
                className="cursor-pointer"
              >
                <Switch isOn={isYearly} size="base" />
              </div>
              <span className="text-sm text-muted flex items-center gap-1">
                Pay annually
                <span className="text-xs bg-info-soft text-info px-1.5 py-0.5 rounded">
                  (17% discount)
                </span>
              </span>
            </div>
          </div>
        )}
        <>
          <div
            className={twMerge("grid gap-4 mx-auto w-full", gridColumnsClass)}
          >
            {filteredPlans.map((plan) => {
              if (plan.tier === BillingTiers.FREE) {
                return null;
              }
              if (plan.tier !== currentPlan.tier && !isUpgrade(plan)) {
                return null;
              }

              return (
                <PlanCard
                  key={plan.tier}
                  plan={plan}
                  currentPlan={currentPlan.tier}
                  isYearly={isYearly}
                  isWorkspaceOwner={isWorkspaceOwner}
                  workspace={workspace}
                  source={source}
                />
              );
            })}
            <PlanCard
              plan={getBillingPlanInfoByTier("custom")}
              currentPlan={currentPlan.tier}
              isYearly={isYearly}
              isWorkspaceOwner={isWorkspaceOwner}
              workspace={workspace}
              source={source}
            />
          </div>

          <div className="flex justify-center mt-4">
            <Button
              variant="link"
              UNSAFE_className="text-muted text-sm"
              onClick={() =>
                window.open(
                  "https://support.replo.app/articles/2238970734-what-replo-plan-is-right-for-me",
                  "_blank",
                )
              }
            >
              Learn what plan is right for you →
            </Button>
          </div>
        </>
      </div>
    </Modal>
  );
};

export const PlanInfoDetails: React.FC<{
  plan: PlanInfo;
  billingInterval: BillingInterval;
}> = ({ plan, billingInterval }) => {
  const { data: billingInfo, isLoading } = useGetBillingInformationQuery({});
  const planInfo = billingInfo && billingInfo[plan.tier];
  const colors = getPlanColorTheme(plan.tier);
  const isYearly = billingInterval === "yearly";

  const price = isYearly ? plan.yearlyPricePerMonth : plan.monthlyPrice;

  return (
    <Card
      className={twMerge("border h-full", colors.cardBorder, colors.cardBg)}
    >
      <CardHeader>
        <CardTitle className="text-3xl font-semibold">
          ${price}
          <span className="text-base font-normal">/month</span>
        </CardTitle>
        <CardDescription className="text-base text-muted">
          Billed {isYearly ? "annually" : "monthly"}
          {isYearly && (
            <span
              className={twMerge(
                "text-xs px-1.5 py-0.5 rounded",
                colors.bg,
                colors.text,
              )}
            >
              Save 17%
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isLoading && planInfo ? (
          <div className="space-y-3">
            {planInfo.features.map((feature) => {
              if (feature.shouldShowInEditor) {
                return (
                  <div
                    key={`feature-${feature.name}`}
                    className="flex items-center gap-2"
                  >
                    <div className="w-5">
                      <Check size={20} className={colors.text} />
                    </div>
                    <span className="text-base">{feature.name}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        ) : (
          <div className="space-y-3 my-6">
            <div className="flex items-center gap-2">
              <div className="w-5">
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
              <Skeleton className="h-5 w-full" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
